/**
 * MEMORIAL DIRECTOR — Remote Review Dashboard
 * Route: /director
 *
 * Cloudflare-native version — ZERO tRPC, ZERO Manus dependencies.
 * All data calls use plain fetch() against the Worker REST API.
 * Auth: better-auth email+password session cookie.
 *
 * Design: "Dark Ops Workspace" — deep charcoal, amber accents, editorial type
 * Layout: Persistent two-panel layout (sidebar project list + main review area)
 *
 * Screens:
 *   1. Login gate — email + password, better-auth session cookie
 *   2. Workspace — sidebar (Active / Archive tabs) + main panel
 *   3. Project detail — 720p draft player, media gallery, manifest EDIT FORM,
 *                       director notes textarea, approve/request-changes actions,
 *                       archive button
 *   4. Archived order detail — read-only record from D1 (R2 may be pruned)
 */
import { useState, useEffect, useRef, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAutosave } from "@/hooks/useAutosave";
import { AutosaveStatusBadge } from "@/components/AutosaveStatusBadge";
import { MusicLibraryModal } from "@/components/MusicLibraryModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Film, Music, ImageIcon, Video, CheckCircle2, AlertCircle, Clock,
  LogOut, Eye, Lock, Play, Pause, RefreshCw, FileText, Calendar, X,
  ChevronDown, ChevronUp, Archive, ArchiveRestore, Save, Pencil,
  Upload, ArrowRightCircle, GripVertical, ListOrdered, RotateCcw,
  Trash2, Plus, Layers, Sliders,
} from "lucide-react";

// ─── API helpers ──────────────────────────────────────────────────────────────
const API = "/api";

async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options.headers ?? {}) },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ─── Types ────────────────────────────────────────────────────────────────────
interface ConfirmAction {
  type: "review" | "archive" | "delete";
  status?: "approved" | "needs_changes";
  label: string;
  projectIdToDelete?: string;
}
interface Order {
  id: number;
  projectId: string;
  tenant: string;
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  dateOfPassing: string | null;
  tagline: string | null;
  tier: string | null;
  tone: string | null;
  musicTrack: string | null;
  musicStyle: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  directorNotes: string | null;
  photoOrderNotes: string | null;
  directorMessage: string | null;
  reviewStatus: string | null;
  deliveryUrl: string | null;
  renderNotes: string | null;
  submittedAt: string;
  archivedAt: string | null;
  updatedAt: string;
  photoFiles: string | null;
  videoFiles: string | null;
  audioFiles: string | null;
}
interface MediaAsset {
  key: string;
  filename: string;
  type: "photo" | "video" | "audio" | "draft_video" | "other";
  size: number;
  url: string;
  bucket: "inbound" | "drafts";
}
interface MediaFile {
  filename: string;
  key: string;
  url: string;
  type: "video" | "audio" | "image" | "other";
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatSize(bytes: number) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function toStringArray(v: string[] | string | null | undefined): string[] {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  try { return JSON.parse(v) as string[]; } catch { return []; }
}
function formatDate(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", {
    year: "numeric", month: "short", day: "numeric",
  });
}
function assetToMediaFile(a: MediaAsset): MediaFile {
  return {
    key: a.key,
    filename: a.filename,
    url: a.url,
    type: a.type === "photo" ? "image"
        : a.type === "video" || a.type === "draft_video" ? "video"
        : a.type === "audio" ? "audio"
        : "other",
  };
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: string }) {
  if (status === "draft_ready")
    return (
      <Badge className="bg-amber-700/20 text-amber-400 border border-amber-700/40 text-[10px] px-1.5 py-0 h-5">
        <Eye className="w-2.5 h-2.5 mr-1" /> Draft Ready
      </Badge>
    );
  if (status === "approved")
    return (
      <Badge className="bg-emerald-700/20 text-emerald-400 border border-emerald-700/40 text-[10px] px-1.5 py-0 h-5">
        <CheckCircle2 className="w-2.5 h-2.5 mr-1" /> Approved
      </Badge>
    );
  if (status === "archived")
    return (
      <Badge className="bg-zinc-700/20 text-zinc-400 border border-zinc-600/40 text-[10px] px-1.5 py-0 h-5">
        <Archive className="w-2.5 h-2.5 mr-1" /> Archived
      </Badge>
    );
  return (
    <Badge className="bg-zinc-700/40 text-zinc-500 border border-zinc-600/40 text-[10px] px-1.5 py-0 h-5">
      <Clock className="w-2.5 h-2.5 mr-1" /> Pending
    </Badge>
  );
}

// ─── Field label helper ───────────────────────────────────────────────────────
function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider block mb-1.5">
      {children}
    </label>
  );
}

// ─── Inline Video Player ─────────────────────────────────────────────────────
function InlineVideoPlayer({ file }: { file: MediaFile }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 overflow-hidden">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 p-3 hover:bg-zinc-800/40 transition-colors text-left group"
      >
        <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center shrink-0">
          <Video className="w-4 h-4 text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-zinc-300 truncate font-medium">{file.filename}</p>
          <p className="text-[10px] text-zinc-600">Click to {expanded ? "collapse" : "preview"}</p>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-zinc-500 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0" />
        )}
      </button>
      {expanded && (
        <div className="px-3 pb-3">
          <video
            src={file.url}
            controls
            className="w-full rounded-lg bg-black"
            style={{ maxHeight: "405px" }}
          />
        </div>
      )}
    </div>
  );
}

// ─── Inline Audio Player ──────────────────────────────────────────────────────
function InlineAudioPlayer({ file }: { file: MediaFile }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const toggle = () => {
    if (!audioRef.current) return;
    playing ? audioRef.current.pause() : audioRef.current.play();
    setPlaying(!playing);
  };
  const formatTime = (s: number) => {
    if (!isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/60 border border-zinc-700/50 hover:border-amber-700/30 transition-colors">
      <audio
        ref={audioRef}
        src={file.url}
        onTimeUpdate={() => { if (audioRef.current) setProgress(audioRef.current.currentTime); }}
        onLoadedMetadata={() => { if (audioRef.current) setDuration(audioRef.current.duration); }}
        onEnded={() => setPlaying(false)}
      />
      <button
        onClick={toggle}
        className="w-8 h-8 rounded-full bg-amber-700/30 hover:bg-amber-700/50 flex items-center justify-center shrink-0 transition-colors"
      >
        {playing ? <Pause className="w-3.5 h-3.5 text-amber-400" /> : <Play className="w-3.5 h-3.5 text-amber-400 ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-zinc-300 truncate font-medium">{file.filename}</p>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex-1 h-1 bg-zinc-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-500 rounded-full transition-all"
              style={{ width: duration > 0 ? `${(progress / duration) * 100}%` : "0%" }}
            />
          </div>
          <span className="text-[10px] text-zinc-500 shrink-0">{formatTime(progress)} / {formatTime(duration)}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Confirm Dialog ───────────────────────────────────────────────────────────
function ConfirmDialog({
  action,
  isPending,
  onConfirm,
  onCancel,
}: {
  action: ConfirmAction;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const isDelete  = action.type === "delete";
  const isArchive = action.type === "archive";
  const isApprove = action.status === "approved";
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-sm p-5 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isDelete  ? "bg-red-950/60 border border-red-800/60"
            : isArchive ? "bg-zinc-800/60 border border-zinc-600/40"
            : isApprove ? "bg-emerald-900/30 border border-emerald-700/40"
            : "bg-amber-900/30 border border-amber-700/40"
          }`}>
            {isDelete  ? <Trash2 className="w-5 h-5 text-red-400" />
            : isArchive ? <Archive className="w-5 h-5 text-zinc-400" />
            : isApprove ? <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            : <AlertCircle className="w-5 h-5 text-amber-400" />}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">{action.label}</h3>
            <p className="text-xs text-zinc-500">
              {isDelete  ? "This will permanently delete ALL R2 files and the DB record. This cannot be undone."
              : isArchive ? "The order record will be saved to the database permanently."
              : isApprove ? "This will notify the operator to begin the final render."
              : "Your notes will be sent to the operator for revision."}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1 h-10 border-zinc-700 text-zinc-400 hover:text-white bg-transparent" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className={`flex-1 h-10 font-semibold ${
              isDelete  ? "bg-red-800 hover:bg-red-700 text-white"
              : isArchive ? "bg-zinc-700 hover:bg-zinc-600 text-white"
              : isApprove ? "bg-emerald-700 hover:bg-emerald-600 text-white"
              : "bg-amber-700 hover:bg-amber-600 text-white"
            }`}
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? "Working…" : isDelete ? "Delete Forever" : "Confirm"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Login Screen ─────────────────────────────────────────────────────────────
function LoginScreen({ onSuccess }: { onSuccess: () => void }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    setLoading(true);
    try {
      await apiFetch("/auth/sign-in/email", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      toast.success("Welcome, Director.");
      onSuccess();
    } catch (err) {
      toast.error((err as Error).message || "Incorrect credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-900/40 to-amber-800/20 border border-amber-700/30 mb-5 shadow-lg shadow-amber-900/10">
            <Lock className="w-7 h-7 text-amber-400" />
          </div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2 tracking-tight">
            Memorial Director
          </h1>
          <p className="text-sm text-zinc-500">Sovereign Factory — Review Portal</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <Input
            type="email"
            placeholder="Director email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-600 h-12 text-base"
            autoFocus
          />
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-600 h-12 text-base"
          />
          <Button
            type="submit"
            disabled={loading || !email.trim() || !password.trim()}
            className="w-full h-12 bg-amber-700 hover:bg-amber-600 text-white font-semibold text-base transition-colors"
          >
            {loading ? "Verifying…" : "Enter Dashboard"}
          </Button>
        </form>
        <p className="text-center text-xs text-zinc-600 mt-6">Session expires after 24 hours</p>
      </div>
    </div>
  );
}

// ─── Media Lightbox ───────────────────────────────────────────────────────────
function Lightbox({ file, onClose }: { file: MediaFile; onClose: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={onClose}>
      <button
        className="absolute top-4 right-4 w-9 h-9 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
        onClick={onClose}
      >
        <X className="w-4 h-4" />
      </button>
      <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        {file.type === "image" && (
          <img src={file.url} alt={file.filename} className="w-full h-auto max-h-[85vh] object-contain rounded-lg" />
        )}
        {file.type === "video" && (
          <video src={file.url} controls autoPlay className="w-full max-h-[85vh] rounded-lg bg-black" />
        )}
        <p className="text-center text-xs text-zinc-500 mt-3">{file.filename}</p>
      </div>
    </div>
  );
}

// ─── Manifest Form State ──────────────────────────────────────────────────────
interface ManifestFormState {
  firstName: string; lastName: string;
  dateOfBirth: string; dateOfPassing: string;
  tagline: string; tier: string; tone: string;
  musicSelection: string; musicTrackKey: string; musicStyle: string;
  contactName: string; contactEmail: string; contactPhone: string;
}
function buildManifestForm(order: Order | null): ManifestFormState {
  return {
    firstName:      order?.firstName      ?? "",
    lastName:       order?.lastName       ?? "",
    dateOfBirth:    order?.dateOfBirth    ?? "",
    dateOfPassing:  order?.dateOfPassing  ?? "",
    tagline:        order?.tagline        ?? "",
    tier:           order?.tier           ?? "",
    tone:           order?.tone           ?? "",
    musicSelection: order?.musicTrack     ?? "",
    musicTrackKey:  order?.musicTrack     ?? "",
    musicStyle:     order?.musicStyle     ?? "",
    contactName:    order?.contactName    ?? "",
    contactEmail:   order?.contactEmail   ?? "",
    contactPhone:   order?.contactPhone   ?? "",
  };
}

// ─── Manifest Form Component ──────────────────────────────────────────────────
function ManifestForm({
  form,
  onChange,
  onSaveDraft,
  isSaving,
}: {
  form: ManifestFormState;
  onChange: (f: ManifestFormState) => void;
  onSaveDraft: () => void;
  isSaving: boolean;
}) {
  const [showMusicModal, setShowMusicModal] = useState(false);
  const set = (field: keyof ManifestFormState) =>
    (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...form, [field]: e.target.value });

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/80 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-800">
        <Pencil className="w-3.5 h-3.5 text-amber-400" />
        <span className="text-xs font-semibold text-zinc-300 uppercase tracking-wider flex-1">
          Manifest — Edit Form
        </span>
        <Button size="sm" onClick={onSaveDraft} disabled={isSaving} className="h-7 px-3 text-xs bg-amber-700/80 hover:bg-amber-700 text-white">
          <Save className="w-3 h-3 mr-1.5" />
          {isSaving ? "Saving…" : "Save Draft"}
        </Button>
      </div>
      <div className="p-4 space-y-4">
        <div>
          <p className="text-[10px] text-zinc-600 uppercase tracking-widest mb-2 font-semibold">Deceased Identity</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel>First Name</FieldLabel>
              <Input value={form.firstName} onChange={set("firstName")} placeholder="e.g. Wayne" className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600 text-sm focus:border-amber-600 h-9" />
            </div>
            <div>
              <FieldLabel>Last Name</FieldLabel>
              <Input value={form.lastName} onChange={set("lastName")} placeholder="e.g. Jolley" className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600 text-sm focus:border-amber-600 h-9" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <FieldLabel>Date of Birth</FieldLabel>
            <Input value={form.dateOfBirth} onChange={set("dateOfBirth")} placeholder="e.g. March 12, 1945" className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600 text-sm focus:border-amber-600 h-9" />
          </div>
          <div>
            <FieldLabel>Date of Passing</FieldLabel>
            <Input value={form.dateOfPassing} onChange={set("dateOfPassing")} placeholder="e.g. January 8, 2025" className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600 text-sm focus:border-amber-600 h-9" />
          </div>
        </div>
        <div>
          <FieldLabel>Tagline / Relationship Descriptor</FieldLabel>
          <Input value={form.tagline} onChange={set("tagline")} placeholder="e.g. Beloved Father, Grandfather & Friend" className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600 text-sm focus:border-amber-600 h-9" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <FieldLabel>Service Tier</FieldLabel>
            <Select value={form.tier || "_none"} onValueChange={(v) => onChange({ ...form, tier: v === "_none" ? "" : v })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white focus:border-amber-600 h-9 text-sm">
                <SelectValue placeholder="Select tier…" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
                <SelectItem value="_none">— Not set —</SelectItem>
                <SelectItem value="slideshow">Slideshow ($49)</SelectItem>
                <SelectItem value="tribute">Tribute ($149)</SelectItem>
                <SelectItem value="legacy">Legacy ($249)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <FieldLabel>Tone / Mood</FieldLabel>
            <Select value={form.tone || "_none"} onValueChange={(v) => onChange({ ...form, tone: v === "_none" ? "" : v })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white focus:border-amber-600 h-9 text-sm">
                <SelectValue placeholder="Select tone…" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
                <SelectItem value="_none">— Not set —</SelectItem>
                <SelectItem value="reverent">Reverent & Dignified</SelectItem>
                <SelectItem value="celebration">Celebration of Life</SelectItem>
                <SelectItem value="humorous">Warm & Humorous</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {/* Music */}
        {showMusicModal && (
          <MusicLibraryModal
            currentSelection={form.musicSelection}
            onSelect={({ title, r2Key }: { title: string; r2Key: string }) =>
              onChange({ ...form, musicSelection: title, musicTrackKey: r2Key })
            }
            onClose={() => setShowMusicModal(false)}
          />
        )}
        <div>
          <FieldLabel>Music Selection</FieldLabel>
          <div className="flex gap-2">
            <Input
              value={form.musicSelection}
              onChange={set("musicSelection")}
              placeholder="No music selected"
              className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600 text-sm focus:border-amber-600 h-9 flex-1"
              readOnly
            />
            <Button type="button" size="sm" variant="outline" onClick={() => setShowMusicModal(true)} className="h-9 border-zinc-700 text-zinc-400 hover:text-white hover:border-amber-600 bg-transparent text-xs">
              <Music className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        <div>
          <FieldLabel>Music Style</FieldLabel>
          <Select value={form.musicStyle || "_none"} onValueChange={(v) => onChange({ ...form, musicStyle: v === "_none" ? "" : v })}>
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white focus:border-amber-600 h-9 text-sm">
              <SelectValue placeholder="Select style…" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
              <SelectItem value="_none">— Not set —</SelectItem>
              <SelectItem value="classical">Classical / Orchestral</SelectItem>
              <SelectItem value="country">Country / Folk</SelectItem>
              <SelectItem value="gospel">Gospel / Hymns</SelectItem>
              <SelectItem value="jazz">Jazz / Blues</SelectItem>
              <SelectItem value="rock">Rock / Classic Rock</SelectItem>
              <SelectItem value="ambient">Ambient / Cinematic</SelectItem>
              <SelectItem value="customer">Customer Upload</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className="text-[10px] text-zinc-600 uppercase tracking-widest mb-2 font-semibold">Primary Contact</p>
          <div className="space-y-2">
            <Input value={form.contactName} onChange={set("contactName")} placeholder="Contact name" className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600 text-sm focus:border-amber-600 h-9" />
            <div className="grid grid-cols-2 gap-2">
              <Input value={form.contactEmail} onChange={set("contactEmail")} placeholder="Email" className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600 text-sm focus:border-amber-600 h-9" />
              <Input value={form.contactPhone} onChange={set("contactPhone")} placeholder="Phone" className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600 text-sm focus:border-amber-600 h-9" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sortable Photo ───────────────────────────────────────────────────────────
interface SortablePhotoProps {
  id: string; file: MediaFile; index: number;
  onLightbox: (f: MediaFile) => void;
  onDelete: (key: string) => void;
}
function SortablePhoto({ id, file, index, onLightbox, onDelete }: SortablePhotoProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : undefined, opacity: isDragging ? 0.6 : 1 };
  return (
    <div ref={setNodeRef} style={style} className={`relative aspect-square rounded-lg overflow-hidden bg-zinc-800 group ${isDragging ? "ring-2 ring-amber-500 shadow-2xl" : ""}`}>
      <div {...attributes} {...listeners} className="absolute top-1 left-1 z-10 w-6 h-6 rounded bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing" title="Drag to reorder">
        <GripVertical className="w-3.5 h-3.5 text-white" />
      </div>
      <div className="absolute top-1 right-1 z-10 w-5 h-5 rounded bg-black/70 flex items-center justify-center">
        <span className="text-[9px] font-bold text-amber-400">{index + 1}</span>
      </div>
      <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(file.key); }} className="absolute bottom-1 right-1 z-10 w-5 h-5 rounded bg-red-900/80 hover:bg-red-700 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" title="Remove from sequence">
        <Trash2 className="w-2.5 h-2.5 text-red-200" />
      </button>
      <button type="button" onClick={() => onLightbox(file)} className="w-full h-full" title={file.filename}>
        <img src={file.url} alt={file.filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" loading="lazy" />
      </button>
    </div>
  );
}

// ─── Draggable Photo Grid ─────────────────────────────────────────────────────
function DraggablePhotoGrid({
  projectId, images, onLightbox, existingSequence, onAddPhoto, pendingAddFile, onPendingAddConsumed,
}: {
  projectId: string; images: MediaFile[]; onLightbox: (f: MediaFile) => void;
  existingSequence: string[]; onAddPhoto?: () => void;
  pendingAddFile?: MediaFile | null; onPendingAddConsumed?: () => void;
}) {
  const [items, setItems] = useState<MediaFile[]>(() => {
    if (existingSequence.length > 0) {
      const byName = new Map(images.map((f) => [f.filename, f]));
      const ordered = existingSequence.map((name) => byName.get(name)).filter((f): f is MediaFile => !!f);
      const inSeq = new Set(existingSequence);
      return [...ordered, ...images.filter((f) => !inSeq.has(f.filename))];
    }
    return images;
  });
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    const buildOrder = (): MediaFile[] => {
      if (existingSequence.length > 0) {
        const byName = new Map(images.map((f) => [f.filename, f]));
        const ordered = existingSequence.map((name) => byName.get(name)).filter((f): f is MediaFile => !!f);
        const inSeq = new Set(existingSequence);
        return [...ordered, ...images.filter((f) => !inSeq.has(f.filename))];
      }
      return images;
    };
    setItems(buildOrder());
    setIsDirty(false);
    setSaveStatus("idle");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, images.length]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems((prev) => {
      const oldIdx = prev.findIndex((f) => f.key === active.id);
      const newIdx = prev.findIndex((f) => f.key === over.id);
      return arrayMove(prev, oldIdx, newIdx);
    });
    setIsDirty(true);
    setSaveStatus("idle");
  };
  const handleSave = async () => {
    setSaveStatus("saving");
    try {
      await apiFetch(`/director/orders/${projectId}/sequence`, {
        method: "POST",
        body: JSON.stringify({ sequence: items.map((f) => f.filename) }),
      });
      setSaveStatus("saved");
      setIsDirty(false);
      setTimeout(() => setSaveStatus("idle"), 3000);
    } catch {
      setSaveStatus("error");
      toast.error("Failed to save photo sequence.");
    }
  };
  const handleReset = () => { setItems(images); setIsDirty(true); setSaveStatus("idle"); };
  const handleDelete = (key: string) => { setItems((prev) => prev.filter((f) => f.key !== key)); setIsDirty(true); setSaveStatus("idle"); };

  useEffect(() => {
    if (!pendingAddFile) return;
    setItems((prev) => {
      if (prev.some((f) => f.key === pendingAddFile.key)) return prev;
      return [...prev, pendingAddFile];
    });
    setIsDirty(true);
    onPendingAddConsumed?.();
  }, [pendingAddFile, onPendingAddConsumed]);

  return (
    <section className="rounded-xl border border-zinc-800 bg-zinc-900/80 p-4">
      <div className="flex items-center gap-2 mb-3">
        <ListOrdered className="w-4 h-4 text-amber-400" />
        <span className="text-xs font-semibold text-zinc-300 uppercase tracking-wider flex-1">Photo Sequence ({items.length})</span>
        <div className="flex items-center gap-1.5">
          {onAddPhoto && (
            <button type="button" onClick={onAddPhoto} className="h-7 px-2.5 rounded-lg text-xs font-medium flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700 transition-colors">
              <Plus className="w-3 h-3" /> Add Photo
            </button>
          )}
          <button type="button" onClick={handleReset} className="h-7 px-2.5 rounded-lg text-xs font-medium flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 border border-zinc-700 transition-colors" title="Reset to original order">
            <RotateCcw className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || saveStatus === "saving"}
            className={`h-7 px-3 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors ${
              isDirty && saveStatus !== "saving"
                ? "bg-amber-700 hover:bg-amber-600 text-white border border-amber-600"
                : saveStatus === "saved"
                ? "bg-emerald-800/40 text-emerald-400 border border-emerald-700/40"
                : "bg-zinc-800 text-zinc-600 border border-zinc-700 cursor-not-allowed"
            }`}
          >
            <Save className="w-3 h-3" />
            {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Saved ✓" : saveStatus === "error" ? "Error" : "Save Sequence"}
          </button>
        </div>
      </div>
      <p className="text-[11px] text-zinc-600 mb-2">Drag thumbnails to reorder. Numbers show render position. Click to preview.</p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((f) => f.key)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-5 sm:grid-cols-7 gap-1.5">
            {items.map((img, idx) => (
              <SortablePhoto key={img.key} id={img.key} file={img} index={idx} onLightbox={onLightbox} onDelete={handleDelete} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </section>
  );
}

// ─── Asset Browser Modal ──────────────────────────────────────────────────────
function AssetBrowserModal({ projectId, onAdd, onClose }: { projectId: string; onAdd: (file: MediaFile) => void; onClose: () => void }) {
  const [assets, setAssets]   = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch<{ assets: MediaAsset[] }>(`/director/orders/${projectId}/media`)
      .then((d) => setAssets(d.assets.filter((a) => a.type === "photo")))
      .catch(() => toast.error("Failed to load media"))
      .finally(() => setLoading(false));
  }, [projectId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-5 py-4 border-b border-zinc-800">
          <Plus className="w-4 h-4 text-amber-400" />
          <h2 className="text-sm font-semibold text-white flex-1">Add Photo from Project</h2>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white transition-colors text-lg leading-none">&times;</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-zinc-500">
              <div className="w-6 h-6 border-2 border-amber-700/40 border-t-amber-500 rounded-full animate-spin" />
            </div>
          ) : !assets.length ? (
            <p className="text-center text-zinc-600 text-sm py-8">No photos found in this project.</p>
          ) : (
            <div className="grid grid-cols-5 sm:grid-cols-7 gap-1.5">
              {assets.map((a) => {
                const f = assetToMediaFile(a);
                return (
                  <button key={a.key} type="button" onClick={() => { onAdd(f); onClose(); }} className="relative aspect-square rounded-lg overflow-hidden bg-zinc-800 hover:ring-2 hover:ring-amber-500 transition-all group" title={`Add ${a.filename}`}>
                    <img src={a.url} alt={a.filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" loading="lazy" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <Plus className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-zinc-800">
          <p className="text-[11px] text-zinc-600">Click a photo to add it to the end of the sequence. Save Sequence to persist.</p>
        </div>
      </div>
    </div>
  );
}

// ─── Project Detail Panel ─────────────────────────────────────────────────────
function ProjectDetail({ projectId, onArchived, onDeleted }: { projectId: string; onArchived: () => void; onDeleted: () => void }) {
  const [order, setOrder]           = useState<Order | null>(null);
  const [assets, setAssets]         = useState<MediaAsset[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [reviewNotes, setReviewNotes] = useState({ directorNotes: "", photoOrderNotes: "", directorMessage: "" });
  const [manifestForm, setManifestForm] = useState<ManifestFormState>(buildManifestForm(null));
  const [lightboxFile, setLightboxFile] = useState<MediaFile | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [showAssetBrowser, setShowAssetBrowser] = useState(false);
  const [pendingAddPhoto, setPendingAddPhoto] = useState<MediaFile | null>(null);
  const [actionPending, setActionPending] = useState(false);
  const [manifestSaving, setManifestSaving] = useState(false);

  // Load order + media
  const loadOrder = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [orderRes, mediaRes] = await Promise.all([
        apiFetch<{ order: Order }>(`/director/orders/${projectId}`),
        apiFetch<{ assets: MediaAsset[] }>(`/director/orders/${projectId}/media`),
      ]);
      setOrder(orderRes.order);
      setAssets(mediaRes.assets);
      const o = orderRes.order;
      setReviewNotes({
        directorNotes:   o.directorNotes   ?? "",
        photoOrderNotes: o.photoOrderNotes ?? "",
        directorMessage: o.directorMessage ?? "",
      });
      setManifestForm(buildManifestForm(o));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { loadOrder(); }, [loadOrder]);

  // Autosave director notes
  const sessionTimestampAdded = useRef(false);
  useEffect(() => { sessionTimestampAdded.current = false; }, [projectId]);

  const handleDirectorNotesChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const rawValue = e.target.value;
    if (!sessionTimestampAdded.current) {
      sessionTimestampAdded.current = true;
      const ts = new Date().toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
      const guardKey = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      const header = `── [${ts}] ──\n`;
      const existing = reviewNotes.directorNotes;
      if (!existing.includes(guardKey)) {
        const separator = existing.trim().length > 0 ? "\n\n" : "";
        setReviewNotes((n) => ({ ...n, directorNotes: existing + separator + header }));
        return;
      }
    }
    setReviewNotes((n) => ({ ...n, directorNotes: rawValue }));
  }, [reviewNotes.directorNotes]);

  const autosaveFn = useCallback(async (notes: string) => {
    await apiFetch(`/director/orders/${projectId}/autosave`, {
      method: "POST",
      body: JSON.stringify({ directorNotes: notes }),
    });
  }, [projectId]);

  const { status: autosaveStatus, lastSavedAt } = useAutosave({
    value: reviewNotes.directorNotes,
    onSave: autosaveFn,
    delay: 1500,
  });

  // Save manifest draft
  const handleSaveDraft = async () => {
    setManifestSaving(true);
    try {
      await apiFetch(`/director/orders/${projectId}/manifest`, {
        method: "POST",
        body: JSON.stringify({
          firstName:    manifestForm.firstName,
          lastName:     manifestForm.lastName,
          dateOfBirth:  manifestForm.dateOfBirth,
          dateOfPassing: manifestForm.dateOfPassing,
          tagline:      manifestForm.tagline,
          tier:         manifestForm.tier,
          tone:         manifestForm.tone,
          musicTrack:   manifestForm.musicTrackKey,
          musicStyle:   manifestForm.musicStyle,
        }),
      });
      toast.success("Manifest saved.");
    } catch (e) {
      toast.error((e as Error).message || "Failed to save manifest");
    } finally {
      setManifestSaving(false);
    }
  };

  // Confirm actions
  const handleConfirm = async () => {
    if (!confirmAction) return;
    setActionPending(true);
    try {
      if (confirmAction.type === "archive") {
        await apiFetch(`/director/orders/${projectId}/archive`, { method: "POST" });
        toast.success("Order archived.");
        setConfirmAction(null);
        onArchived();
        return;
      }
      if (confirmAction.type === "delete") {
        await apiFetch(`/director/orders/${confirmAction.projectIdToDelete ?? projectId}`, { method: "DELETE" });
        toast.success("Project deleted.");
        setConfirmAction(null);
        onDeleted();
        return;
      }
      // Review action (approved or needs_changes)
      await apiFetch(`/director/orders/${projectId}/review`, {
        method: "POST",
        body: JSON.stringify({
          reviewStatus:    confirmAction.status!,
          directorNotes:   reviewNotes.directorNotes,
          photoOrderNotes: reviewNotes.photoOrderNotes,
          directorMessage: reviewNotes.directorMessage,
        }),
      });
      // If approved, also fire the submit webhook
      if (confirmAction.status === "approved") {
        await apiFetch(`/director/orders/${projectId}/submit`, { method: "POST" });
        toast.success("✅ Approved — render triggered.");
      } else {
        toast("✏️ Changes requested — operator notified.");
      }
      await loadOrder();
    } catch (e) {
      toast.error((e as Error).message || "Action failed");
    } finally {
      setActionPending(false);
      setConfirmAction(null);
    }
  };

  if (loading) return (
    <div className="flex-1 flex items-center justify-center text-zinc-500">
      <div className="w-8 h-8 border-2 border-amber-700/40 border-t-amber-500 rounded-full animate-spin" />
    </div>
  );
  if (error) return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-3" />
        <p className="text-sm text-zinc-400">{error}</p>
        <Button size="sm" variant="outline" onClick={loadOrder} className="mt-3 border-zinc-700 text-zinc-400 hover:text-white bg-transparent">Retry</Button>
      </div>
    </div>
  );
  if (!order) return null;

  const photos    = assets.filter((a) => a.type === "photo").map(assetToMediaFile);
  const videos    = assets.filter((a) => a.type === "video").map(assetToMediaFile);
  const audios    = assets.filter((a) => a.type === "audio").map(assetToMediaFile);
  const drafts    = assets.filter((a) => a.type === "draft_video").map(assetToMediaFile);
  const existingSeq = toStringArray(order.photoFiles);

  return (
    <div className="flex-1 overflow-y-auto">
      {lightboxFile && <Lightbox file={lightboxFile} onClose={() => setLightboxFile(null)} />}
      {showAssetBrowser && (
        <AssetBrowserModal
          projectId={projectId}
          onAdd={(f) => setPendingAddPhoto(f)}
          onClose={() => setShowAssetBrowser(false)}
        />
      )}
      {confirmAction && (
        <ConfirmDialog
          action={confirmAction}
          isPending={actionPending}
          onConfirm={handleConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0d0d0d]/95 backdrop-blur-sm border-b border-zinc-800/60 px-5 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <h2 className="font-['Playfair_Display'] text-lg font-bold text-white truncate">
            {[order.firstName, order.lastName].filter(Boolean).join(" ") || "Unnamed Order"}
          </h2>
          <div className="flex items-center gap-2 mt-0.5">
            <StatusBadge status={order.reviewStatus ?? "pending"} />
            <span className="text-[10px] text-zinc-600">{formatDate(order.submittedAt)}</span>
            {order.tier && <span className="text-[10px] text-zinc-600 capitalize">· {order.tier}</span>}
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={loadOrder} className="h-8 border-zinc-700 text-zinc-400 hover:text-white bg-transparent">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>

      <div className="p-5 space-y-5">
        {/* Draft video player */}
        {drafts.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-2">
              <Film className="w-4 h-4 text-amber-400" />
              <span className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Draft Render ({drafts.length})</span>
            </div>
            <div className="space-y-2">
              {drafts.map((f) => <InlineVideoPlayer key={f.key} file={f} />)}
            </div>
          </section>
        )}

        {/* Videos */}
        {videos.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-2">
              <Video className="w-4 h-4 text-zinc-400" />
              <span className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Customer Videos ({videos.length})</span>
            </div>
            <div className="space-y-2">
              {videos.map((f) => <InlineVideoPlayer key={f.key} file={f} />)}
            </div>
          </section>
        )}

        {/* Audio */}
        {audios.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-2">
              <Music className="w-4 h-4 text-zinc-400" />
              <span className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Audio Files ({audios.length})</span>
            </div>
            <div className="space-y-1.5">
              {audios.map((f) => <InlineAudioPlayer key={f.key} file={f} />)}
            </div>
          </section>
        )}

        {/* Photo sequence */}
        {photos.length > 0 && (
          <DraggablePhotoGrid
            projectId={projectId}
            images={photos}
            onLightbox={setLightboxFile}
            existingSequence={existingSeq}
            onAddPhoto={() => setShowAssetBrowser(true)}
            pendingAddFile={pendingAddPhoto}
            onPendingAddConsumed={() => setPendingAddPhoto(null)}
          />
        )}

        {/* Manifest form */}
        <ManifestForm
          form={manifestForm}
          onChange={setManifestForm}
          onSaveDraft={handleSaveDraft}
          isSaving={manifestSaving}
        />

        {/* Director notes */}
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/80 p-5 space-y-3">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-amber-400" />
            <span className="text-xs font-semibold text-zinc-300 uppercase tracking-wider flex-1">Director Notes</span>
            <AutosaveStatusBadge status={autosaveStatus} lastSavedAt={lastSavedAt} />
          </div>
          <Textarea
            value={reviewNotes.directorNotes}
            onChange={handleDirectorNotesChange}
            placeholder="Production notes, observations, special instructions…"
            className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600 text-sm focus:border-amber-600 min-h-[120px] resize-none font-mono text-xs"
          />
          <p className="text-[10px] text-zinc-700">Autosaves after 1.5 s of inactivity. A timestamp is appended on first keystroke of each session.</p>
        </div>

        {/* Review form */}
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/80 p-5 space-y-4">
          <div>
            <h3 className="font-['Playfair_Display'] text-base font-semibold text-white mb-0.5">Director Review</h3>
            <p className="text-xs text-zinc-500">Notes are saved to the project and sent to the operator.</p>
          </div>
          <div>
            <FieldLabel>Photo Order / Sequencing Notes</FieldLabel>
            <Textarea
              value={reviewNotes.photoOrderNotes}
              onChange={(e) => setReviewNotes((n) => ({ ...n, photoOrderNotes: e.target.value }))}
              placeholder="e.g. Start with garden photo. Group childhood shots. End with family reunion."
              className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600 text-sm focus:border-amber-600 min-h-[80px] resize-none"
            />
          </div>
          <div>
            <FieldLabel>Message to Operator</FieldLabel>
            <Textarea
              value={reviewNotes.directorMessage}
              onChange={(e) => setReviewNotes((n) => ({ ...n, directorMessage: e.target.value }))}
              placeholder="Any special requests, concerns, or instructions for the final render…"
              className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600 text-sm focus:border-amber-600 min-h-[80px] resize-none"
            />
          </div>
          <div className="space-y-2 pt-1">
            <Button onClick={() => setConfirmAction({ type: "review", status: "approved", label: "Approve — Send to Final Render" })} disabled={actionPending} className="w-full h-11 bg-emerald-700 hover:bg-emerald-600 text-white font-semibold transition-colors">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Approve — Send to Final Render
            </Button>
            <Button onClick={() => setConfirmAction({ type: "review", status: "needs_changes", label: "Request Changes" })} disabled={actionPending} variant="outline" className="w-full h-11 border-zinc-700 text-zinc-300 hover:text-white hover:border-amber-600 bg-transparent transition-colors">
              <AlertCircle className="w-4 h-4 mr-2" /> Request Changes
            </Button>
            <p className="text-[11px] text-zinc-700 text-center">A confirmation step will appear before saving.</p>
          </div>
          <div className="border-t border-zinc-800 pt-3 space-y-2">
            <Button onClick={() => setConfirmAction({ type: "archive", label: "Archive This Order" })} disabled={actionPending} variant="outline" className="w-full h-9 border-zinc-700/60 text-zinc-500 hover:text-zinc-300 hover:border-zinc-600 bg-transparent text-xs transition-colors">
              <Archive className="w-3.5 h-3.5 mr-2" /> Archive This Order
            </Button>
            <Button onClick={() => setConfirmAction({ type: "delete", label: "Permanently Delete Project", projectIdToDelete: projectId })} disabled={actionPending} variant="outline" className="w-full h-9 border-red-900/60 text-red-700 hover:text-red-400 hover:border-red-700 bg-transparent text-xs transition-colors">
              <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete Project
            </Button>
            <p className="text-[10px] text-zinc-700 text-center">Archive saves to DB. Delete removes everything permanently.</p>
          </div>
        </div>

        {/* Project ID */}
        <div className="px-3 py-2 rounded-lg bg-zinc-900/40 border border-zinc-800/60">
          <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-0.5">Project ID</p>
          <p className="font-mono text-xs text-zinc-400 break-all">{projectId}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Archived Order Detail ────────────────────────────────────────────────────
function ArchivedOrderDetail({ projectId }: { projectId: string }) {
  const [order, setOrder]   = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ order: Order }>(`/director/orders/${projectId}`)
      .then((d) => setOrder(d.order))
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [projectId]);

  if (loading) return (
    <div className="flex-1 flex items-center justify-center text-zinc-500">
      <div className="w-8 h-8 border-2 border-amber-700/40 border-t-amber-500 rounded-full animate-spin" />
    </div>
  );
  if (error || !order) return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-3" />
        <p className="text-sm text-zinc-400">{error ?? "Order not found"}</p>
      </div>
    </div>
  );

  const pf = toStringArray(order.photoFiles);
  const vf = toStringArray(order.videoFiles);
  const af = toStringArray(order.audioFiles);

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="sticky top-0 z-10 bg-[#0d0d0d]/95 backdrop-blur-sm border-b border-zinc-800/60 px-5 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <h2 className="font-['Playfair_Display'] text-lg font-bold text-white truncate">
            {[order.firstName, order.lastName].filter(Boolean).join(" ") || "Unnamed Order"}
          </h2>
          <div className="flex items-center gap-2 mt-0.5">
            <StatusBadge status="archived" />
            <span className="text-[10px] text-zinc-600">Archived {formatDate(order.archivedAt)}</span>
          </div>
        </div>
      </div>
      <div className="p-5 space-y-4">
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4 grid grid-cols-2 gap-4">
          {[
            ["Name", [order.firstName, order.lastName].filter(Boolean).join(" ") || "—"],
            ["Born", order.dateOfBirth || "—"],
            ["Passed", order.dateOfPassing || "—"],
            ["Tagline", order.tagline || "—"],
            ["Tier", order.tier || "—"],
            ["Tone", order.tone || "—"],
            ["Music", order.musicTrack || order.musicStyle || "—"],
            ["Contact", order.contactName || "—"],
          ].map(([label, value]) => (
            <div key={label}>
              <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1">{label}</p>
              <p className="text-sm text-zinc-300">{value}</p>
            </div>
          ))}
        </div>
        {(order.directorNotes || order.photoOrderNotes || order.directorMessage) && (
          <div className="rounded-xl border border-amber-800/20 bg-amber-900/5 p-4 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs font-semibold text-amber-300 uppercase tracking-wider">Director Notes</span>
            </div>
            {order.directorNotes && <p className="text-sm text-zinc-300 whitespace-pre-wrap">{order.directorNotes}</p>}
            {order.photoOrderNotes && (
              <div>
                <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1">Photo Order</p>
                <p className="text-sm text-zinc-400 whitespace-pre-wrap">{order.photoOrderNotes}</p>
              </div>
            )}
            {order.directorMessage && (
              <div>
                <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1">Message to Operator</p>
                <p className="text-sm text-zinc-400 whitespace-pre-wrap">{order.directorMessage}</p>
              </div>
            )}
          </div>
        )}
        {(pf.length > 0 || vf.length > 0 || af.length > 0) && (
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4">
            <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-3 font-semibold">Media Inventory (filenames only — R2 files may be pruned)</p>
            {pf.length > 0 && (
              <div className="mb-2">
                <p className="text-[10px] text-zinc-500 mb-1">Photos ({pf.length})</p>
                <div className="flex flex-wrap gap-1">
                  {pf.map((f: string) => <span key={f} className="text-[10px] bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded font-mono">{f}</span>)}
                </div>
              </div>
            )}
            {vf.length > 0 && (
              <div className="mb-2">
                <p className="text-[10px] text-zinc-500 mb-1">Videos ({vf.length})</p>
                <div className="flex flex-wrap gap-1">
                  {vf.map((f: string) => <span key={f} className="text-[10px] bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded font-mono">{f}</span>)}
                </div>
              </div>
            )}
            {af.length > 0 && (
              <div>
                <p className="text-[10px] text-zinc-500 mb-1">Audio ({af.length})</p>
                <div className="flex flex-wrap gap-1">
                  {af.map((f: string) => <span key={f} className="text-[10px] bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded font-mono">{f}</span>)}
                </div>
              </div>
            )}
          </div>
        )}
        <div className="px-3 py-2 rounded-lg bg-zinc-900/40 border border-zinc-800/60">
          <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-0.5">Project ID</p>
          <p className="font-mono text-xs text-zinc-400 break-all">{order.projectId}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Main Workspace ───────────────────────────────────────────────────────────
type SidebarTab = "active" | "archive";
type SelectedItem = { type: "active"; projectId: string } | { type: "archived"; projectId: string } | null;

function Workspace({ onLogout }: { onLogout: () => void }) {
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>("active");
  const [selected, setSelected]     = useState<SelectedItem>(null);
  const [activeOrders, setActiveOrders]   = useState<Order[]>([]);
  const [archivedOrders, setArchivedOrders] = useState<Order[]>([]);
  const [activeLoading, setActiveLoading]   = useState(true);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [activeError, setActiveError] = useState<string | null>(null);

  const fetchActive = useCallback(async () => {
    setActiveLoading(true);
    setActiveError(null);
    try {
      const d = await apiFetch<{ orders: Order[] }>("/director/orders");
      setActiveOrders(d.orders.filter((o) => o.reviewStatus !== "archived"));
    } catch (e) {
      setActiveError((e as Error).message);
    } finally {
      setActiveLoading(false);
    }
  }, []);

  const fetchArchived = useCallback(async () => {
    setArchivedLoading(true);
    try {
      const d = await apiFetch<{ orders: Order[] }>("/director/orders/archived");
      setArchivedOrders(d.orders);
    } catch {
      // non-fatal
    } finally {
      setArchivedLoading(false);
    }
  }, []);

  useEffect(() => { fetchActive(); }, [fetchActive]);
  useEffect(() => { if (sidebarTab === "archive") fetchArchived(); }, [sidebarTab, fetchArchived]);

  const handleRefresh = () => { fetchActive(); if (sidebarTab === "archive") fetchArchived(); };

  const handleLogout = async () => {
    try {
      await apiFetch("/auth/sign-out", { method: "POST" });
    } catch { /* ignore */ }
    onLogout();
    toast("Signed out.");
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      {/* Top bar */}
      <header className="border-b border-zinc-800/60 px-5 py-3 flex items-center gap-3 shrink-0">
        <div className="flex items-center gap-2.5 flex-1">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-900/40 to-amber-800/20 border border-amber-700/30 flex items-center justify-center">
            <Film className="w-3.5 h-3.5 text-amber-400" />
          </div>
          <span className="font-['Playfair_Display'] text-base font-bold text-white">Memorial Director</span>
          <span className="text-[10px] text-zinc-600 hidden sm:block">Sovereign Factory</span>
        </div>
        <button onClick={handleRefresh} className="w-8 h-8 rounded-lg bg-zinc-900 border border-zinc-800 hover:border-zinc-700 flex items-center justify-center text-zinc-500 hover:text-zinc-300 transition-colors" title="Refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
        <button onClick={handleLogout} className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-zinc-900 border border-zinc-800 hover:border-zinc-700 text-zinc-500 hover:text-zinc-300 text-xs transition-colors">
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-72 border-r border-zinc-800/60 flex flex-col shrink-0 overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-zinc-800/60 shrink-0">
            {(["active", "archive"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setSidebarTab(tab)}
                className={`flex-1 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                  sidebarTab === tab
                    ? "text-amber-400 border-b-2 border-amber-500 bg-amber-900/5"
                    : "text-zinc-600 hover:text-zinc-400"
                }`}
              >
                {tab === "active" ? `Active (${activeOrders.length})` : `Archive (${archivedOrders.length})`}
              </button>
            ))}
          </div>

          {/* Order list */}
          <div className="flex-1 overflow-y-auto">
            {sidebarTab === "active" && (
              <>
                {activeLoading && (
                  <div className="flex items-center justify-center py-12 text-zinc-600">
                    <div className="w-5 h-5 border-2 border-zinc-700 border-t-zinc-500 rounded-full animate-spin" />
                  </div>
                )}
                {activeError && <p className="px-4 py-6 text-xs text-red-500 text-center">{activeError}</p>}
                {!activeLoading && !activeError && activeOrders.length === 0 && (
                  <div className="px-4 py-10 text-center text-zinc-600">
                    <Film className="w-7 h-7 mx-auto mb-2 opacity-20" />
                    <p className="text-xs">No active orders.</p>
                  </div>
                )}
                {activeOrders.map((order) => (
                  <button
                    key={order.projectId}
                    onClick={() => setSelected({ type: "active", projectId: order.projectId })}
                    className={`w-full text-left px-4 py-3 border-b border-zinc-800/40 transition-colors group ${
                      selected?.projectId === order.projectId && selected?.type === "active"
                        ? "bg-zinc-800/30 border-l-2 border-l-amber-600"
                        : "hover:bg-zinc-800/20 border-l-2 border-l-transparent"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span className="text-xs text-zinc-300 font-medium truncate leading-tight">
                        {[order.firstName, order.lastName].filter(Boolean).join(" ") || order.projectId}
                      </span>
                      <StatusBadge status={order.reviewStatus ?? "pending"} />
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-zinc-600">
                      <Calendar className="w-2.5 h-2.5" />
                      <span>{formatDate(order.submittedAt)}</span>
                      {order.tier && <><span>·</span><span className="capitalize">{order.tier}</span></>}
                    </div>
                  </button>
                ))}
              </>
            )}

            {sidebarTab === "archive" && (
              <>
                {archivedLoading && (
                  <div className="flex items-center justify-center py-12 text-zinc-600">
                    <div className="w-5 h-5 border-2 border-zinc-700 border-t-zinc-500 rounded-full animate-spin" />
                  </div>
                )}
                {!archivedLoading && archivedOrders.length === 0 && (
                  <div className="px-4 py-10 text-center text-zinc-600">
                    <ArchiveRestore className="w-7 h-7 mx-auto mb-2 opacity-20" />
                    <p className="text-xs">No archived orders yet.</p>
                  </div>
                )}
                {archivedOrders.map((order) => (
                  <button
                    key={order.projectId}
                    onClick={() => setSelected({ type: "archived", projectId: order.projectId })}
                    className={`w-full text-left px-4 py-3 border-b border-zinc-800/40 transition-colors group ${
                      selected?.projectId === order.projectId && selected?.type === "archived"
                        ? "bg-zinc-800/30 border-l-2 border-l-zinc-500"
                        : "hover:bg-zinc-800/20 border-l-2 border-l-transparent"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span className="text-xs text-zinc-300 font-medium truncate leading-tight">
                        {[order.firstName, order.lastName].filter(Boolean).join(" ") || order.projectId}
                      </span>
                      <StatusBadge status="archived" />
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-zinc-600">
                      <Calendar className="w-2.5 h-2.5" />
                      <span>{formatDate(order.archivedAt)}</span>
                      {order.tier && <><span>·</span><span className="capitalize">{order.tier}</span></>}
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>
        </aside>

        {/* Main panel */}
        <main className="flex-1 overflow-hidden flex flex-col">
          {selected?.type === "active" && (
            <ProjectDetail
              projectId={selected.projectId}
              onArchived={() => { setSelected(null); setSidebarTab("archive"); fetchArchived(); }}
              onDeleted={() => { setSelected(null); fetchActive(); }}
            />
          )}
          {selected?.type === "archived" && (
            <ArchivedOrderDetail projectId={selected.projectId} />
          )}
          {!selected && (
            <div className="flex-1 flex items-center justify-center text-zinc-600">
              <div className="text-center max-w-xs">
                <div className="w-14 h-14 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center mx-auto mb-4">
                  <Film className="w-6 h-6 text-zinc-700" />
                </div>
                <p className="text-sm font-medium text-zinc-500 mb-1">Select an order to review</p>
                <p className="text-xs text-zinc-700">Choose a project from the sidebar. Use the Archive tab to recall completed orders.</p>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

// ─── Main Director Page ───────────────────────────────────────────────────────
export default function Director() {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    // Check if we have a valid better-auth session
    apiFetch("/auth/get-session")
      .then((d: any) => setAuthenticated(!!d?.user))
      .catch(() => setAuthenticated(false));
  }, []);

  if (authenticated === null) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-zinc-700 border-t-amber-600 rounded-full animate-spin" />
      </div>
    );
  }
  if (!authenticated) {
    return <LoginScreen onSuccess={() => setAuthenticated(true)} />;
  }
  return <Workspace onLogout={() => setAuthenticated(false)} />;
}
